import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery, pagedResponse } from './pagination.js';
import { IsoDateSchema, MessageSchema } from './primitives.js';
const c = initContract();
/**
 * Leads (PART 10, CRM-01) — the earliest sales-pipeline stage: a separate,
 * temporary entity that ends in exactly one of two places (converted into an
 * opportunity, or disqualified with a reason) and never becomes a business
 * partner by itself.
 *
 * Permission area: `crm` (PART 16.2.2 row 5), per action:
 *   list/get = crm.view · create (Quick capture) = crm.create · advance,
 *   convert, disqualify, edit, log activity = crm.edit (CRM-01 §9's "lead
 *   status permission" and "lead activity permission" resolve to the crm
 *   area's edit action — the closed 12x5 vocabulary has no finer grain, and
 *   the Accountant's RCU row exercises exactly this shape).
 *
 * Owner ruling R1 (restated by CRM-01 §9): there are NO view modes. Every
 * capability is a server-checked permission; overdue/due-soon are computed
 * here at read time (BR-CRM-01-05, BR-CRM-01-06) and never stored.
 *
 * CONVERT (BR-CRM-01-12, BR-CRM-01-13, BR-CRM-01-14) creates in one transaction: the partner the
 * company becomes (found by name, or created — Q-CRM-01-6/Q-CRM-02-5's
 * reconciliation: the deal joins the real partner list), the opportunity in
 * the FIRST configured stage at its default probability with value 0, a FULL
 * copy of the activity log plus one conversion entry, and the Converted stamp
 * with the new code on the lead.
 */
// ── Vocabularies (CRM-01 §5) ────────────────────────────────────────────────
/** Field 7: "Seven choices, and no others." */
export const LEAD_SOURCES = [
    'website',
    'referral',
    'cold_call',
    'trade_show',
    'ad_campaign',
    'walk_in',
    'other',
];
/** Field 9 / §7: the five statuses; the last two only through modals. */
export const LEAD_STATUSES = ['new', 'contacted', 'qualified', 'converted', 'disqualified'];
/** Field 14: the five disqualification reasons. */
export const LEAD_DISQUALIFY_REASONS = [
    'not_interested',
    'no_budget',
    'bad_timing',
    'duplicate',
    'other',
];
/** English labels for stored log sentences (display localizes the enums). */
export const LEAD_REASON_LABELS = {
    not_interested: 'Not Interested',
    no_budget: 'No Budget',
    bad_timing: 'Bad Timing',
    duplicate: 'Duplicate',
    other: 'Other',
};
export const LEAD_STATUS_LABELS = {
    new: 'New',
    contacted: 'Contacted',
    qualified: 'Qualified',
    converted: 'Converted',
    disqualified: 'Disqualified',
};
// ── The fixed sentences of CRM-01 ───────────────────────────────────────────
export const LEAD_MESSAGES = {
    /**
     * BR-CRM-01-01 — the create refusal when called some other way, and the
     * server floor under all three of ERR-CRM-01-01, ERR-CRM-01-02, ERR-CRM-01-03, which are one grey
     * button described from three sides (nothing typed, a name only, and the
     * click that does nothing).
     */
    incomplete: 'A lead needs a name, one contact method (phone or email) and a source.',
    /** CRM-01 §7: converted and disqualified are finished; there is no reopen. */
    finished: 'This lead is finished. A converted or disqualified lead cannot be changed.',
    /**
     * BR-CRM-01-07 / ERR-CRM-01-07: Advance can never reach Converted or
     * Disqualified, which is why no Advance button is drawn on a Qualified card.
     */
    advanceEnds: 'Advance moves a lead only through New → Contacted → Qualified.',
    /** BR-CRM-01-10 / ERR-CRM-01-04 — the disqualify gate. */
    reasonRequired: 'Choose a reason before disqualifying this lead.',
    /** Q-CRM-01-2 (proposed default, built): warn on save, then save anyway. */
    duplicateContact: (code) => `Another lead (${code}) already has this phone or email.`,
    /** The activity composer refuses an empty entry (§6: "Does nothing when empty"). */
    emptyActivity: 'Type something before adding it to the log.',
};
/** The stored log sentences (BR-CRM-01-03, BR-CRM-01-11, §6 drag rule 1). */
export const LEAD_LOG = {
    created: 'Lead created via quick capture.',
    statusMoved: (statusLabel) => `Status moved to ${statusLabel}.`,
    disqualified: (reasonLabel) => `Disqualified — ${reasonLabel}`,
    converted: (oppCode) => `Converted to opportunity ${oppCode}.`,
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const LeadSourceSchema = z.enum(LEAD_SOURCES);
export const LeadStatusSchema = z.enum(LEAD_STATUSES);
export const LeadDisqualifyReasonSchema = z.enum(LEAD_DISQUALIFY_REASONS);
export const LeadActivitySchema = z
    .object({
    id: z.uuid(),
    kind: z.enum(['note', 'call', 'email', 'meeting', 'system']),
    body: z.string(),
    createdBy: z.uuid().nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.string(),
})
    .strict();
export const LeadSchema = z
    .object({
    id: z.uuid(),
    /** LEAD-#### (field 2). */
    code: z.string(),
    leadNo: z.number().int().positive(),
    name: z.string(),
    company: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    source: LeadSourceSchema,
    salespersonId: z.uuid().nullable(),
    salespersonName: z.string().nullable(),
    status: LeadStatusSchema,
    /** 0–100; 0 means "not scored" and displays as a dash (BR-CRM-01-04). */
    score: z.number().int().min(0).max(100),
    nextFollowUp: IsoDateSchema.nullable(),
    disqualifyReason: LeadDisqualifyReasonSchema.nullable(),
    convertedOpportunityId: z.uuid().nullable(),
    convertedOppCode: z.string().nullable(),
    /** BR-CRM-01-05: active + follow-up date before today. Never on finished leads. */
    overdue: z.boolean(),
    /** BR-CRM-01-06: active + follow-up today..today+2. */
    dueSoon: z.boolean(),
    /** Whole days past the follow-up date when overdue, else null. */
    daysOverdue: z.number().int().nullable(),
    activityCount: z.number().int(),
    createdAt: z.string(),
})
    .strict();
export const LeadDetailSchema = LeadSchema.extend({
    /** Newest first. Never edited, only added to (field 13). */
    activities: LeadActivitySchema.array(),
}).strict();
export const CreateLeadRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    company: z.string().trim().max(120).optional(),
    phone: z.string().trim().min(1).max(40).optional(),
    email: z.string().trim().min(1).max(254).optional(),
    source: LeadSourceSchema,
    salespersonId: z.uuid().optional(),
    /** Clamped server-side to 0–100 (Q-CRM-01-1's ruling on BR-CRM-01-04). */
    score: z.number().int().min(0).max(100).optional(),
    nextFollowUp: IsoDateSchema.optional(),
})
    .strict();
/** The editable working fields — status moves only through the actions. */
export const UpdateLeadRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(120).optional(),
    company: z.string().trim().max(120).nullable().optional(),
    phone: z.string().trim().max(40).nullable().optional(),
    email: z.string().trim().max(254).nullable().optional(),
    source: LeadSourceSchema.optional(),
    salespersonId: z.uuid().nullable().optional(),
    score: z.number().int().min(0).max(100).optional(),
    nextFollowUp: IsoDateSchema.nullable().optional(),
})
    .strict();
export const DisqualifyLeadRequestSchema = z
    .object({
    reason: LeadDisqualifyReasonSchema,
    /** "plus the optional note text" (BR-CRM-01-11). */
    note: z.string().trim().max(2000).optional(),
})
    .strict();
export const LogLeadActivityRequestSchema = z
    .object({
    kind: z.enum(['note', 'call', 'email', 'meeting']),
    body: z.string().trim().min(1).max(2000),
})
    .strict();
export const LeadWriteResponseSchema = z
    .object({
    lead: LeadDetailSchema,
    /** Q-CRM-01-2: duplicate phone/email warns and then saves. */
    warnings: z.string().array(),
})
    .strict();
/**
 * CRM-01's METRIC STRIP and §5d's source performance, as ONE small read.
 *
 * They are here rather than folded out of the list because they are figures
 * about EVERY lead, and the list is now a page. `PAGINATION.md` §3 step 10
 * names this exactly: "a screen may derive something from the WHOLE list
 * without saying so", and a page turns each of those numbers into a quiet lie.
 * `GET /api/items/stats` is the same shape for the same reason.
 *
 * `converted` is carried per source rather than a rate, because §5d prints
 * BOTH a volume share and a conversion rate from it, and a rate that has
 * already been rounded cannot produce the other.
 */
export const LeadStatsSchema = z
    .object({
    byStatus: z.record(LeadStatusSchema, z.number().int()),
    /** BR-CRM-01-05's computed flag, counted over every active lead. */
    overdue: z.number().int(),
    sources: z
        .object({ source: LeadSourceSchema, count: z.number().int(), converted: z.number().int() })
        .strict()
        .array(),
})
    .strict();
const leadParams = z.object({ leadId: z.uuid() }).strict();
export const leadsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/crm/leads',
        query: pagedQuery({
            q: z.string().optional(),
            status: LeadStatusSchema.optional(),
            source: LeadSourceSchema.optional(),
            salespersonId: z.uuid().optional(),
            /** The Overdue view: BR-CRM-01-05's computed flag as a filter. */
            overdue: z.literal('true').optional(),
        }),
        responses: {
            200: pagedResponse('leads', LeadSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Leads (crm.view), PAGED (NFR-42). Overdue/due-soon are computed at read time and never stored — so `overdue` is a DERIVED filter, and `LEAD_OVERDUE` is that derivation in SQL, beside the function it mirrors.',
    },
    stats: {
        method: 'GET',
        path: '/api/crm/leads/stats',
        responses: { 200: LeadStatsSchema, 401: MessageSchema, 403: MessageSchema },
        summary: 'The metric strip and §5d source performance (crm.view) — aggregates over EVERY lead, so a page of the list above cannot narrow them.',
    },
    get: {
        method: 'GET',
        path: '/api/crm/leads/:leadId',
        pathParams: leadParams,
        responses: {
            200: LeadDetailSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One lead with its full activity log (crm.view).',
    },
    create: {
        method: 'POST',
        path: '/api/crm/leads',
        body: CreateLeadRequestSchema,
        responses: {
            201: LeadWriteResponseSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Quick capture (crm.create): name + one contact + source (BR-CRM-01-01); starts New with the automatic log entry (BR-CRM-01-03); duplicate contact warns and saves (Q-CRM-01-2).',
    },
    update: {
        method: 'PATCH',
        path: '/api/crm/leads/:leadId',
        pathParams: leadParams,
        body: UpdateLeadRequestSchema,
        responses: {
            200: LeadWriteResponseSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit an ACTIVE lead’s working fields (crm.edit). A finished lead answers 409 — there is no reopen (CRM-01 §7).',
    },
    advance: {
        method: 'POST',
        path: '/api/crm/leads/:leadId/advance',
        pathParams: leadParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: LeadDetailSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'One step: New → Contacted → Qualified, never further (BR-CRM-01-07, crm.edit). Logs "Status moved to <status>."',
    },
    convert: {
        method: 'POST',
        path: '/api/crm/leads/:leadId/convert',
        pathParams: leadParams,
        body: z.object({}).strict().optional(),
        responses: {
            201: z
                .object({
                lead: LeadDetailSchema,
                opportunityId: z.uuid(),
                opportunityCode: z.string(),
                partnerId: z.uuid(),
            })
                .strict(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Convert (crm.edit): creates the partner (found by name or created) and ONE opportunity — "<company or name> · <lead name>", value 0, FIRST configured stage at its default probability, the lead’s salesperson, the WHOLE activity log copied plus a conversion entry — then stamps the lead Converted (BR-CRM-01-12, BR-CRM-01-13, BR-CRM-01-14).',
    },
    disqualify: {
        method: 'POST',
        path: '/api/crm/leads/:leadId/disqualify',
        pathParams: leadParams,
        body: DisqualifyLeadRequestSchema,
        responses: {
            200: LeadDetailSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Disqualify (crm.edit): a reason from the five is mandatory (BR-CRM-01-10); logs "Disqualified — <reason>" plus the optional note (BR-CRM-01-11).',
    },
    logActivity: {
        method: 'POST',
        path: '/api/crm/leads/:leadId/activities',
        pathParams: leadParams,
        body: LogLeadActivityRequestSchema,
        responses: {
            201: LeadDetailSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Add a note/call/email/meeting to the log (crm.edit). Append-only forever.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=leads.js.map